import React, { useState, useEffect } from 'react';
import { X, Search, Building2, Loader2 } from 'lucide-react';
import { fetchSecondBusinessOptions } from '../../services/contentService';
import { createBusinessOptions } from '../../data/optionsData';
import { useLanguage } from '../../context/LanguageContext';

const SecondBusinessSelectionModal = ({
  showModal,
  setShowModal,
  onBusinessSelect,
  theme,
  userPrimaryBusiness
}) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessOptions, setBusinessOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load business options from frontend data (all 42 types)
  useEffect(() => {
    if (showModal) {
      loadBusinessOptions();
    }
  }, [showModal]);

  const loadBusinessOptions = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use frontend data instead of API to get all 42 business types
      const frontendOptions = createBusinessOptions(t);
      const apiOptions = await fetchSecondBusinessOptions().catch(() => []);
      
      // Combine frontend data with API data (prioritize frontend data)
      const combinedOptions = frontendOptions.map(frontendOption => {
        // Find matching API option for additional data
        const apiMatch = apiOptions.find(apiOption => 
          apiOption.name?.toLowerCase() === frontendOption.type?.toLowerCase()
        );
        
        return {
          id: apiMatch?.id || frontendOption.type,
          name: frontendOption.label,
          type: frontendOption.type,
          image: frontendOption.image,
          category: apiMatch?.category || 'General',
          description: apiMatch?.description || `Get help with ${frontendOption.label.toLowerCase()}`,
          url: apiMatch?.url || frontendOption.image
        };
      });
      
      setBusinessOptions(combinedOptions);
    } catch (err) {
      console.error('Failed to load second business options:', err);
      setError(err.message || 'Failed to load business options');
      // Fallback to frontend data only
      const frontendOptions = createBusinessOptions(t);
      const fallbackOptions = frontendOptions.map(option => ({
        id: option.type,
        name: option.label,
        type: option.type,
        image: option.image,
        category: 'General',
        description: `Get help with ${option.label.toLowerCase()}`,
        url: option.image
      }));
      setBusinessOptions(fallbackOptions);
    } finally {
      setLoading(false);
    }
  };

  // Get business options
  const allBusinessOptions = businessOptions;

  // Filter business options based on search term
  const filteredBusinesses = allBusinessOptions.filter((option) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return (
      !normalizedSearch ||
      option.name?.toLowerCase().includes(normalizedSearch) ||
      option.category?.toLowerCase().includes(normalizedSearch)
    );
  });

  const handleBusinessClick = (business) => {
    setSelectedBusiness(business);
  };

  const handleConfirmSelection = () => {
    if (selectedBusiness) {
      onBusinessSelect(selectedBusiness.name);
      setShowModal(false);
      setSelectedBusiness(null);
      setSearchTerm('');
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedBusiness(null);
    setSearchTerm('');
  };

  if (!showModal) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '32px'
    }}>
      <div style={{
        background: theme.modalBg || '#ffffff',
        borderRadius: '24px',
        maxWidth: 'min(960px, 95vw)',
        width: '100%',
        maxHeight: '88vh',
        border: `1px solid ${theme.modalBorder || '#e5e7eb'}`,
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '32px 32px 24px 32px',
          borderBottom: `1px solid ${theme.primaryBorder || '#e5e7eb'}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '12px',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Building2 size={20} color="#fff" />
              </div>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: theme.primaryText || '#1f2937',
                margin: 0
              }}>
                {t('fullchat.addSecondBusiness')}
              </h2>
            </div>
            <button
              onClick={handleClose}
              style={{
                background: theme.closeButtonBg || '#f3f4f6',
                border: `1px solid ${theme.closeButtonBorder || '#d1d5db'}`,
                color: theme.closeButtonText || '#6b7280',
                padding: '8px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = theme.closeButtonHover || '#e5e7eb';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = theme.closeButtonBg || '#f3f4f6';
              }}
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Description */}
          <p style={{
            margin: 0,
            color: theme.secondaryText || '#6b7280',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {t('fullchat.chooseSecondBusiness', { business: userPrimaryBusiness })}
          </p>
        </div>

        {/* Search Bar */}
        <div style={{
          padding: '24px 32px 16px 32px',
          borderBottom: `1px solid ${theme.primaryBorder || '#e5e7eb'}`
        }}>
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Search 
              size={20} 
              color="#9ca3af" 
              style={{ 
                position: 'absolute', 
                left: '12px', 
                zIndex: 1 
              }} 
            />
            <input
              type="text"
              placeholder={t('fullchat.searchBusinessTypes')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 44px',
                border: `1px solid ${theme.primaryBorder || '#d1d5db'}`,
                borderRadius: '12px',
                fontSize: '14px',
                background: theme.inputBg || '#ffffff',
                color: theme.primaryText || '#1f2937',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#10b981';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.primaryBorder || '#d1d5db';
              }}
            />
          </div>
        </div>
        
        {/* Business Options Grid */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '32px'
        }}>
          {loading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              height: '100%',
              color: theme.secondaryText || '#6b7280'
            }}>
              <div style={{
                width: 40,
                height: 40,
                border: `4px solid ${theme.primaryBorder || '#d1d5db'}`,
                borderTopColor: '#10b981',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span style={{ fontWeight: 600 }}>{t('businessModal.loadingBusinessOptions')}</span>
            </div>
          )}

          {!loading && error && (
            <div style={{
              background: theme.errorBg || 'rgba(248,113,113,0.12)',
              border: `1px solid ${theme.errorBorder || 'rgba(248,113,113,0.4)'}`,
              color: theme.errorText || '#ef4444',
              borderRadius: 16,
              padding: 24,
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{t('businessModal.unableToLoad')}</p>
              <p style={{ margin: '8px 0 0 0', fontSize: 14 }}>{error}</p>
              <button
                onClick={loadBusinessOptions}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 16,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '10px 18px',
                  borderRadius: '999px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.85';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {t('businessModal.tryAgain')}
              </button>
            </div>
          )}

          {!loading && !error && filteredBusinesses.length === 0 && (
            <div style={{
              background: theme.cardBg || '#ffffff',
              border: `1px solid ${theme.primaryBorder || '#e5e7eb'}`,
              borderRadius: 16,
              padding: 32,
              textAlign: 'center',
              color: theme.secondaryText || '#6b7280'
            }}>
              <Search size={40} color="#d1d5db" style={{ marginBottom: 16 }} />
              <p style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{t('businessModal.noBusinessTypesFound')}</p>
              <p style={{ margin: '8px 0 0 0', fontSize: 14 }}>{t('businessModal.tryAdjustingSearch')}</p>
            </div>
          )}

          {!loading && !error && filteredBusinesses.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '24px'
            }}>
              {filteredBusinesses.map((option) => {
                const isSelected =
                  (selectedBusiness?.id && option.id === selectedBusiness.id) ||
                  selectedBusiness?.name === option.name;

                return (
                  <button
                    key={option.id || option.name}
                    onClick={() => handleBusinessClick(option)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      background: theme.cardBg || '#ffffff',
                      border: `2px solid ${isSelected ? '#059669' : theme.primaryBorder || '#e5e7eb'}`,
                      borderRadius: '20px',
                      color: theme.primaryText || '#1f2937',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      overflow: 'hidden',
                      textAlign: 'left',
                      minHeight: 260,
                      boxShadow: isSelected ? '0 12px 30px rgba(16,185,129,0.25)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      if (!isSelected) {
                        e.currentTarget.style.boxShadow = '0 12px 30px rgba(16,185,129,0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = isSelected
                        ? '0 12px 30px rgba(16,185,129,0.25)'
                        : 'none';
                    }}
                  >
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      height: 160,
                      background: 'linear-gradient(135deg, #10b981, #059669)'
                    }}>
                      {option.url ? (
                        <img
                          src={option.url}
                          alt={option.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextSibling;
                            if (fallback) {
                              fallback.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: option.url ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 8,
                        color: '#fff',
                        background: 'linear-gradient(135deg, rgba(16,185,129,0.7), rgba(5,150,105,0.7))'
                      }}>
                        <Building2 size={32} aria-hidden="true" />
                      </div>
                      {isSelected && (
                        <div style={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 14,
                          fontWeight: 700,
                          color: '#059669'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>

                    <div style={{
                      padding: '20px 22px 22px 22px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12
                    }}>
                      <h3 style={{
                        margin: 0,
                        fontSize: 18,
                        fontWeight: 700,
                        color: theme.primaryText || '#1f2937'
                      }}>
                        {option.name}
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: 14,
                        color: theme.secondaryText || '#6b7280',
                        lineHeight: 1.5
                      }}>
                        {option.size ? `${option.size} • ` : ''}Second business option
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with Action Buttons */}
        <div style={{
          padding: '24px 32px 32px 32px',
          borderTop: `1px solid ${theme.primaryBorder || '#e5e7eb'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: `1px solid ${theme.primaryBorder || '#d1d5db'}`,
              color: theme.secondaryText || '#6b7280',
              padding: '12px 24px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = theme.cardHoverBg || '#f9fafb';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {t('common.cancel')}
          </button>

          <button
            onClick={handleConfirmSelection}
            disabled={!selectedBusiness}
            style={{
              background: selectedBusiness
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : '#d1d5db',
              color: '#ffffff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: selectedBusiness ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              opacity: selectedBusiness ? 1 : 0.6
            }}
            onMouseEnter={e => {
              if (selectedBusiness) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3)';
              }
            }}
            onMouseLeave={e => {
              if (selectedBusiness) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {t('fullchat.addSecondBusiness')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecondBusinessSelectionModal;
